import RailsNestedForm from "./rails_nested_form_controller"

export default class extends RailsNestedForm {
  static targets = ["modal", "newForm", "champ", "moveModal", "moveSelect"];
  static values = { newClassGroup: String, selectClassMessage: String };

  #rowToMove = null
  #runGroupOptionsForMove = []
  #autoNamedRunGroup = null

  // runs before rails-nested-form#remove, while the class is still in the run group
  champRemoving({ target }) {
    const runGroup = target.closest(".run-group-fields")
    this.#autoNamedRunGroup = this.#getRunGroupName(runGroup) === this.#expectedNameFromClasses(runGroup) ? runGroup : null
  }

  // when a class is removed, update the champ IDs and name for the run group and hide the run group if it's empty
  champRemoved({ target }) {
    const runGroup = target.closest(".run-group-fields")
    this.#updateChampIds(runGroup)
    if (this.#autoNamedRunGroup === runGroup) this.#setRunGroupName(runGroup, this.#expectedNameFromClasses(runGroup))
    this.#autoNamedRunGroup = null
    const visibleRows = runGroup.querySelectorAll(".event_segment_series:not([style*='display: none'])")
    if (visibleRows.length === 0) runGroup.style.display = "none"
  }

  // set up the move class modal and open it
  openMoveModal(e) {
    e.preventDefault()
    this.#rowToMove = e.target.closest(".event_segment_series")
    const sourceRunGroup = this.#rowToMove.closest(".run-group-fields")

    this.#runGroupOptionsForMove = Array.from(this.element.querySelectorAll(".run-group-fields"))
      .filter(rg => rg !== sourceRunGroup && rg.style.display !== "none")

    this.moveSelectTarget.innerHTML = ""
    this.moveSelectTarget.appendChild(new Option(this.newClassGroupValue, "new"))
    if (this.#runGroupOptionsForMove.length > 0) {
      const separator = new Option("--------", "")
      separator.disabled = true
      this.moveSelectTarget.appendChild(separator)
    }
    this.#runGroupOptionsForMove.forEach((rg, i) => {
      const name = rg.querySelector("[name*='run_groups_attributes'][name*='[name]']")?.value || `Class Group ${i + 1}`
      const option = new Option(name, i)
      this.moveSelectTarget.appendChild(option)
    })
    this.moveModalTarget.querySelector(".class-to-move").textContent = this.#rowToMove.querySelector(".champ_name").textContent
    this.moveModalTarget.querySelector(".class-run-group").textContent = sourceRunGroup.querySelector("[name*='[name]']").value

    $(this.moveModalTarget).modal('show')
  }

  // move the class to the selected run group
  confirmMove(e) {
    e.preventDefault()
    if (!this.#rowToMove) return

    let targetRunGroup
    if (this.moveSelectTarget.value === "new") {
      const className = this.#rowToMove.querySelector(".champ_name").textContent.trim()
      targetRunGroup = this.#createNewRunGroupForMove(className, this.#rowToMove.closest(".run-group-fields"))
    } else {
      targetRunGroup = this.#runGroupOptionsForMove[parseInt(this.moveSelectTarget.value)]
    }

    if (!targetRunGroup) return

    this.#moveChampRow(this.#rowToMove, targetRunGroup)
    $(this.moveModalTarget).modal('hide')

    this.#rowToMove = null
    this.#runGroupOptionsForMove = []
  }

  // update the available champs list before opening the new run group modal
  openModal(e) {
    e.preventDefault()
    this.#updateAvailableChamps()
    $(this.modalTarget).modal('show')
  }

  // add a new run group to the form
  new(e) {
    e.preventDefault()

    if (!this.#validateNewForm()) return

    this.add(e)
    this.#addNewRunGroup()

    // jquery :( for modal handling
    $(this.modalTarget).modal('hide')
    this.newFormTarget.reset()
    // reset() doesn't fire change, so nudge checkbox-overlap to re-enable everything
    this.newFormTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // validate the new run group form
  #validateNewForm() {
    // validate that at least one checkbox was selected, be it a class or a class group
    const firstCheckbox = this.newFormTarget.querySelector("[name='run_group[champ_ids][]']")
    if (firstCheckbox) {
      const valid = this.#checkedCheckboxes().length > 0
      firstCheckbox.setCustomValidity(valid ? "" : this.selectClassMessageValue)
    }

    if (!this.newFormTarget.checkValidity()) {
      this.newFormTarget.reportValidity()
      return false
    }

    return true
  }

  #addNewRunGroup() {
    // get the newly created form
    const inserted = this.targetTarget.previousElementSibling
    // get the "submitted" form
    const data = new FormData(this.newFormTarget)

    // captured before the form is cleared by reset() in new()
    const selectedChamps = this.#selectedChamps()
    const selectedNames = this.#selectedNames()

    // populate the newly created form with the data
    for (let [name, value] of data.entries()) {
      // if the name is empty, create a name from the selected classes and class groups
      if (name === "run_group[name]" && value.trim() === "") {
        value = this.#joinClassNames(selectedNames)
      }

      // source: "run_group[name]" → target: "event[run_groups_attributes][1234567890][name]"
      const field = name.match(/\[(\w+)\]$/)?.[1]
      if (!field) continue

      const input = inserted.querySelector(`[name*="[${field}]"]`)
      if (input) input.value = value
    }

    inserted.querySelector("[name*='[champ_ids]']").value = selectedChamps.map(champ => champ.id).join(",")

    // delay so that the controller exists for the classes so we can add to it
    requestAnimationFrame(() => this.#addChampRows(inserted, selectedChamps, data))
  }

  #addChampRows(inserted, selectedChamps, formData) {
    const div = inserted.querySelector("[data-controller='rails-nested-form']")
    const controller = this.application.getControllerForElementAndIdentifier(div, "rails-nested-form")

    for (const champ of selectedChamps) {
      // trigger the nested form add method and then populate the fields
      controller.add(new Event("click"))
      const row = controller.targetTarget.previousElementSibling
      row.querySelector("[name*='[champ_id]']").value = champ.id
      row.querySelector(".champ_name").textContent = champ.name
      row.querySelector("[name*='[entry_form_id]']").value = formData.get("run_group[entry_form_id]")
    }
  }

  #createNewRunGroupForMove(name, sourceRunGroup) {
    // add the run group as normal
    this.add(new Event("click"))
    const inserted = this.targetTarget.previousElementSibling
    // set the name based on the class
    inserted.querySelector("[name*='[name]']").value = name
    // update the max entries if possible
    const sourceMaxEntries = sourceRunGroup?.querySelector("[name*='[max_entries]']")?.value
    if (sourceMaxEntries) inserted.querySelector("[name*='[max_entries]']").value = sourceMaxEntries
    return inserted
  }

  #moveChampRow(row, targetRunGroup) {
    const sourceRunGroup = row.closest(".run-group-fields")
    const sourceDiv = row.closest("[data-controller='rails-nested-form']")

    const sourceName = this.#getRunGroupName(sourceRunGroup)
    const targetName = this.#getRunGroupName(targetRunGroup)
    const sourceOldExpected = this.#expectedNameFromClasses(sourceRunGroup)
    const targetOldExpected = this.#expectedNameFromClasses(targetRunGroup)

    const target = targetRunGroup.querySelector("[data-rails-nested-form-target='target']")
    target.insertAdjacentElement("beforebegin", row)

    // update the champ IDs for the target run group
    this.#updateChampIds(targetRunGroup)
    // update the champ IDs for the source run group
    if (sourceRunGroup) this.#updateChampIds(sourceRunGroup)

    if (sourceRunGroup && sourceName === sourceOldExpected) {
      this.#setRunGroupName(sourceRunGroup, this.#expectedNameFromClasses(sourceRunGroup))
    }
    if (targetName === targetOldExpected) {
      this.#setRunGroupName(targetRunGroup, this.#expectedNameFromClasses(targetRunGroup))
    }

    // dispatch so champRemoved can hide the source group if it's now empty
    if (sourceDiv) sourceDiv.dispatchEvent(new CustomEvent("rails-nested-form:remove", { bubbles: true }))
  }

  #getRunGroupName(runGroupEl) {
    return runGroupEl?.querySelector("[name*='run_groups_attributes'][name*='[name]']")?.value || ""
  }

  #setRunGroupName(runGroupEl, name) {
    const field = runGroupEl.querySelector("[name*='run_groups_attributes'][name*='[name]']")
    if (field) field.value = name
  }

  #expectedNameFromClasses(runGroupEl) {
    return this.#joinClassNames(
      Array.from(runGroupEl?.querySelectorAll(".event_segment_series:not([style*='display: none']) .champ_name") || [])
        .map(el => el.textContent.trim())
    )
  }

  #joinClassNames(names) {
    return [...names].sort().join(" / ")
  }

  #updateChampIds(runGroupEl) {
    const champIds = Array.from(runGroupEl.querySelectorAll(".event_segment_series:not([style*='display: none']) [name*='[champ_id]']"))
      .map(input => input.value)
      .filter(Boolean)
    const field = runGroupEl.querySelector("[name*='[champ_ids]']")
    if (field) field.value = champIds.join(",")
  }

  // the checked class / class group checkboxes in the new run group modal
  #checkedCheckboxes() {
    return Array.from(this.newFormTarget.querySelectorAll("input[type=checkbox][data-champ-ids]:checked"))
  }

  // the champ IDs a checkbox carries (one for a class, a comma-separated list for a class group)
  #champIds(checkbox) {
    return (checkbox.dataset.champIds || "").split(",").map(id => id.trim()).filter(Boolean)
  }

  // the champs to add to a new run group: checked individual classes plus the champs
  // belonging to any checked class group, deduped and in classes-table order
  #selectedChamps() {
    // id -> name lookup from the individual class checkboxes
    const champNames = new Map(
      this.champTargets.map(td => [
        td.querySelector("input[type=checkbox]").value,
        td.querySelector("label").textContent.trim(),
      ])
    )

    // gather champ ids from every checked checkbox
    const selectedIds = new Set()
    this.#checkedCheckboxes().forEach(checkbox => {
      this.#champIds(checkbox).forEach(id => selectedIds.add(id))
    })

    return Array.from(champNames.keys())
      .filter(id => selectedIds.has(id))
      .map(id => ({ id, name: champNames.get(id) }))
  }

  // the names used to auto-name a run group: a checked class group contributes its
  // own name (not its classes), a checked individual class contributes its name
  #selectedNames() {
    return this.#checkedCheckboxes()
      .map(checkbox => this.#checkboxName(checkbox))
      .filter(Boolean)
  }

  #checkboxName(checkbox) {
    const label = checkbox.closest("td")?.querySelector("label")
    if (!label) return ""

    // class group labels carry a help-block listing their classes — exclude it
    const clone = label.cloneNode(true)
    clone.querySelectorAll(".help-block").forEach(el => el.remove())
    return clone.textContent.trim()
  }

  #updateAvailableChamps() {
    // find all champ IDs that are already in a run group
    const usedIds = new Set(
      Array.from(this.element.querySelectorAll("[name*='[champ_id]']"))
        .filter(input => input.closest(".event_segment_series")?.style.display !== "none")
        .map(input => input.value)
        .filter(Boolean)
    )

    // hide any class or class group whose champs are already in a run group, restriping each table
    this.newFormTarget.querySelectorAll("table").forEach(table => {
      let stripeIndex = 0
      table.querySelectorAll("tr").forEach(row => {
        const checkbox = row.querySelector("input[type=checkbox][data-champ-ids]")
        if (!checkbox) return

        const unavailable = this.#champIds(checkbox).some(id => usedIds.has(id))
        row.classList.toggle("champ-unavailable", unavailable)
        if (!unavailable) {
          row.classList.toggle("odd", stripeIndex % 2 === 0)
          row.classList.toggle("even", stripeIndex % 2 !== 0)
          stripeIndex++
        }
      })

      // hide the whole class group field when none of its groups are available
      if (table.querySelector(".class-group-checkbox")) {
        const wrapper = table.closest(".control-group")
        if (wrapper) wrapper.style.display = stripeIndex === 0 ? "none" : ""
      }
    })
  }
}
